import './GridHeader.css';

import { useEffect, useId, useRef, useMemo, useState } from 'react';
import {
  browsersOutline,
  downloadOutline,
  refreshOutline,
  searchOutline,
} from 'ionicons/icons';
import DatasetButtons from '../DatasetButtons/DatasetButtons';
import FunctionalButtonsRow, {
  FunctionalButtonConfig,
} from '../FunctionalButtonsRow/FunctionalButtonsRow';
import { useTranslate } from '../../../hooks/context/useTranslate';
import HeaderLabel from './components/HeaderLabel/HeaderLabel';
import Peersets from './components/Peersets/Peersets';
import { useDatasetContext } from '../../../hooks/context/useDatasetContext';
import { useSelectedRowsContext } from '../../../hooks/context/useSelectedRowsContext';
import { getDatasetButtonsWrapperInlineStyle } from '../../../utils/getDatasetButtonsWrapperInlineStyle';
import { getExportOptions } from '../../../utils/getExportOptions';
import { getExportPopoverKey } from '../../../utils/getExportPopoverKey';
import { useIsCompact } from '../FunctionalButtonsRow/hooks/useIsCompact';
import ExportPopover from './components/ExportPopover/ExportPopover';
import { canExportDataset } from '../../../utils/canExportDataset';

interface GridHeaderProps {
  toggleDetail: () => void;
  openExport: (exportMode: string) => void;
  refetchData: () => void;
  openFilter: () => void;
  setGridHeaderHeight: (value: number) => void;
  disableDetail: boolean;
  reloadData: () => void;
}

const GridHeader = ({
  toggleDetail,
  openExport,
  refetchData,
  openFilter,
  setGridHeaderHeight,
  disableDetail,
  reloadData,
}: GridHeaderProps) => {
  /** Inline min-width on dataset-buttons-wrapper so dataset stops shrinking at trigger size, leaving remaining shrink for functional. */
  const [datasetMinWidth, setDatasetMinWidth] = useState(0);
  const [functionalHiddenState, setFunctionalHiddenState] = useState({
    hidden: 0,
    totalHideable: 0,
  });

  const headerRowRef = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslate();
  const {
    dataset,
    data,
    datasetData,
    activeFilters,
    activeSorting,
    isGridLoading,
  } = useDatasetContext();
  const { selectedRows, expandedRow, selectedVirtualRows } =
    useSelectedRowsContext();
  const datasetLocked =
    functionalHiddenState.totalHideable > 0 && functionalHiddenState.hidden > 0;

  const isCompact = useIsCompact();
  const exportTriggerId = `dataset-export-trigger-${useId().replace(/:/g, '')}`;
  const hasSubsets =
    dataset !== undefined &&
    Array.isArray(dataset.subsets) &&
    dataset.subsets.length > 0;
  const exportAllowed = canExportDataset(dataset);
  const disabledExport = isGridLoading || !data || data.length === 0;

  const functionalButtons: FunctionalButtonConfig[] = useMemo(() => {
    const buttons: FunctionalButtonConfig[] = [
      {
        key: 'reload',
        label: t('reload'),
        icon: refreshOutline,
        onClick: reloadData,
        disabled: isGridLoading,
        hidePriority: 1,
        className: 'header-btn',
      },
      {
        key: 'detail',
        label: t('detail'),
        icon: browsersOutline,
        onClick: toggleDetail,
        disabled: disableDetail || isGridLoading,
        alwaysVisible: true,
      },
      {
        key: 'filter',
        label: t('detailedFilter'),
        icon: searchOutline,
        onClick: openFilter,
        disabled: isGridLoading,
        alwaysVisible: true,
        className: 'header-btn',
      },
    ];
    if (exportAllowed) {
      buttons.unshift({
        key: 'export',
        label: t('export.title'),
        icon: downloadOutline,
        onClick: () => {
          if (!hasSubsets) openExport('main');
        },
        disabled: disabledExport,
        hidePriority: 2,
        className: 'header-btn',
        ...(hasSubsets && {
          id: exportTriggerId,
          overflowChildren: getExportOptions(t, openExport),
        }),
      });
    }
    return buttons;
  }, [
    t,
    reloadData,
    isGridLoading,
    toggleDetail,
    disableDetail,
    openFilter,
    hasSubsets,
    disabledExport,
    exportTriggerId,
    openExport,
    exportAllowed,
  ]);

  const datasetWrapperStyle = getDatasetButtonsWrapperInlineStyle(
    datasetMinWidth,
    datasetLocked
  );
  const ref = useRef<HTMLDivElement | any>(null);

  useEffect(() => {
    if (!ref.current) return;
    const resizeObserver = new ResizeObserver(() => {
      setGridHeaderHeight(ref.current?.clientHeight);
    });
    resizeObserver.observe(ref.current);
    return () => resizeObserver.disconnect();
  }, []);

  if (!dataset) return null;

  return (
    <div ref={ref}>
      <Peersets className="header" />
      <div className="header" ref={headerRowRef}>
        <HeaderLabel />
        <div className="dataset-buttons-wrapper" style={datasetWrapperStyle}>
          <DatasetButtons
            activeFilters={activeFilters}
            activeSorting={activeSorting}
            data={data}
            dataset={dataset}
            datasetData={datasetData}
            selectedVirtualRows={selectedVirtualRows}
            refetchData={refetchData}
            selectedRows={selectedRows}
            currentRow={expandedRow}
            onOverflowChange={(state) =>
              setDatasetMinWidth((previous) =>
                previous === state.collapsedWidth
                  ? previous
                  : state.collapsedWidth
              )
            }
          />
        </div>
        <FunctionalButtonsRow
          buttons={functionalButtons}
          headerRowRef={headerRowRef}
          popoverLabel={t('display')}
          onHiddenChange={(state) =>
            setFunctionalHiddenState((previous) =>
              previous.hidden === state.hidden &&
              previous.totalHideable === state.totalHideable
                ? previous
                : state
            )
          }
        />
        {hasSubsets && (
          <ExportPopover
            key={getExportPopoverKey(functionalHiddenState.hidden, isCompact)}
            triggerId={exportTriggerId}
            onExport={openExport}
          />
        )}
      </div>
    </div>
  );
};

export default GridHeader;
